<template>
	<div v-if="!isLoading && titlesToDisplay?.length" class="top-10-titles-container mt-4">
		<h2 v-if="showTitle" class="title section-title" :class="{ 'hide-sm': showMobileTitle }">
			{{ titleText }}
		</h2>

		<div class="top-10-title-list">
			<Top10HorizontalTitleListGraphql
				:titles="titlesToDisplay"
				:isBlocking="isLoading"
				:showMobileTitle="showMobileTitle"
				:titleText="titleText"
				:preventDefault="true"
				:showQuickActions="true"
				:hasMoreLikeThis="true"
				:showSeenList="true"
				:showPosition="true"
				:provider="provider"
				@onTitleClick="trackTitleClick($event)"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import type Provider from '@/interfaces/provider';
import type { HorizontalTitleListItem } from '@/components/timeline/types';

import {
	ObjectType,
	Platform,
	PopularTitlesSorting,
	PopularityRankTypes,
	StreamingChartObjectType,
} from '@/@types/graphql-types';
import { PopularTabType } from '@/interfaces/titles';

import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getSelectedTab } from '@/components/popular/provider-module/helper';
import Top10HorizontalTitleListGraphql from '@/components/popular/provider-module/Top10HorizontalTitleListGraphql.vue';

import {
	GetProviderTop10TitlesDocument,
	type GetProviderTop10TitlesQuery,
	type GetProviderTop10TitlesQueryVariables,
} from './queries/GetProviderTop10Titles.query';

import {
	GetProviderTop10TitlesFallbackDocument,
	type GetProviderTop10TitlesFallbackQuery,
	type GetProviderTop10TitlesFallbackQueryVariables,
} from './queries/GetProviderTop10TitlesFallback.query';

import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { computed } from 'vue';

const props = withDefaults(
	defineProps<{
		provider: Provider;
		selectedTab: PopularTabType;
		showTitle?: boolean;
		showMobileTitle?: boolean;
	}>(),
	{
		showTitle: true,
		showMobileTitle: false,
	}
);

const { language, country } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();

useProvideClickoutPlacement('top_10');

const top10TitlesObjectTypes = computed(() => {
	switch (props.selectedTab) {
		case PopularTabType.MOVIE:
			return [ObjectType.Movie];

		case PopularTabType.SHOW:
			return [ObjectType.Show];

		case PopularTabType.ALL:
			return [ObjectType.Show, ObjectType.Movie];

		default:
			return [];
	}
});

const top10ProviderMovieTitles = useQuery<GetProviderTop10TitlesQuery, GetProviderTop10TitlesQueryVariables>(
	GetProviderTop10TitlesDocument,
	() => ({
		after: '',
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
		},
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			objectType: StreamingChartObjectType.Movie,
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			nextTitles: 0,
			previousTitles: 0,
			packages: [props.provider.shortName],
		},
	}),
	{ fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' }
);

const top10ProviderShowTitles = useQuery<GetProviderTop10TitlesQuery, GetProviderTop10TitlesQueryVariables>(
	GetProviderTop10TitlesDocument,
	() => ({
		after: '',
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
		},
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			objectType: StreamingChartObjectType.Show,
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			nextTitles: 0,
			previousTitles: 0,
			packages: [props.provider.shortName],
		},
	}),
	{ fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' }
);

const top10FallbackTitles = useQuery<GetProviderTop10TitlesFallbackQuery, GetProviderTop10TitlesFallbackQueryVariables>(
	GetProviderTop10TitlesFallbackDocument,
	() => ({
		after: '',
		popularTitlesFilter: {
			packages: [props.provider.shortName],
			objectTypes: top10TitlesObjectTypes.value,
		},
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
			preAffiliate: true,
		},
		popularTitlesSortBy: PopularTitlesSorting.Trending,
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			objectType:
				top10TitlesObjectTypes.value.length === 1 ? (top10TitlesObjectTypes.value[0] as any) : undefined,
			nextTitles: 0,
			previousTitles: 0,
		},
	}),
	{ fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' }
);

const isLoading = computed(
	() =>
		top10ProviderMovieTitles.loading.value ||
		top10ProviderShowTitles.loading.value ||
		top10FallbackTitles.loading.value
);

const top10MixedTitles = computed(() => {
	const mixedTitles = top10ProviderMovieTitles?.result.value?.streamingCharts.edges?.concat(
		top10ProviderShowTitles?.result.value?.streamingCharts.edges || []
	);

	const sortedMixedTitles = mixedTitles?.sort((a: any, b: any) => {
		if (a?.streamingChartInfo?.rank && b?.streamingChartInfo?.rank) {
			const rankScoreA = a?.streamingChartInfo?.rank * 0.99;
			const rankScoreB = b?.streamingChartInfo?.rank * 0.99;
			const daysInTop10ScoreA = a?.streamingChartInfo?.daysInTop10 * 0.01;
			const daysInTop10ScoreB = b?.streamingChartInfo?.daysInTop10 * 0.01;

			const scoreA = rankScoreA + daysInTop10ScoreA;
			const scoreB = rankScoreB + daysInTop10ScoreB;

			a = {
				...a,
				score: scoreA,
			};

			b = {
				...b,
				score: scoreB,
			};

			return a.score - b.score;
		}
		return 0;
	});

	return sortedMixedTitles?.slice(0, 10);
});

const titleNodes = computed(() => {
	if (props.selectedTab === PopularTabType.MOVIE) {
		return top10ProviderMovieTitles.result.value?.streamingCharts.edges?.map((el: { node: any }) => el.node);
	}

	if (props.selectedTab === PopularTabType.SHOW) {
		return top10ProviderShowTitles.result.value?.streamingCharts.edges?.map((el: { node: any }) => el.node);
	}

	return top10MixedTitles.value?.map((el: { node: any }) => el.node);
});

const fallbackTitleNodes = computed(() => {
	return top10FallbackTitles.result.value?.popularTitles.edges
		?.map((el: { node: any }) => el.node)
		.sort(
			(
				a: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } },
				b: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } }
			) => {
				const position_a = a.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
				const position_b = b.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
				return position_a - position_b;
			}
		);
});

const titlesToDisplay = computed(() => (titleNodes.value?.length !== 10 ? fallbackTitleNodes.value : titleNodes.value));

const titleText = computed(() => {
	switch (props.selectedTab) {
		case PopularTabType.ALL:
			return t('WEBAPP_PROVIDER_PAGE_TOP_TITLES', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		case PopularTabType.MOVIE:
			return t('WEBAPP_PROVIDER_PAGE_TOP_MOVIES', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		case PopularTabType.SHOW:
			return t('WEBAPP_PROVIDER_PAGE_TOP_TV_SHOWS', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		default:
			return '';
	}
});

function trackTitleClick({ index, title }: { index: number; title: HorizontalTitleListItem }) {
	const selectedTab = getSelectedTab(activeRoute?.value.name);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: 'top-10-titles',
			property: selectedTab,
			value: index,
		},
		[new SnowplowTitleContextGraphql(title.objectId, title.objectType)]
	);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins.scss';
.top-10-titles-container {
	.title {
		color: #fff;
		font-size: 20px;
		font-weight: 600;
		margin-top: 15px;
		margin: 12px 0 24px 0;
		&.hide-sm {
			@include sm() {
				display: none;
			}
		}
	}
}

.top-10-title-list {
	:deep(.horizontal-title-list__item--with-position) {
		margin-bottom: 0px;
		padding-bottom: 0px;
	}
	:deep(.hidden-horizontal-scrollbar__items) {
		overflow-y: hidden;
	}
}

.blocking-element {
	height: 250px;
	width: 100%;
	@include sm() {
		height: 480px;
	}
}

.fallback {
	text-align: center;
	margin: 20px 0;

	h3,
	p {
		margin: 5px 0;
	}
}
</style>
